import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { registry, strategies } from '../data/registry';
import { buildIntradayCurve, SIZING_DEFAULT } from '../lib/intradayCurve';
import eosPnl from '../data/strategies/eos_pnl.json';
import artemisPnl from '../data/strategies/artemis_pnl.json';
import heliosPnl from '../data/strategies/helios_pnl.json';
import hyperionPnl from '../data/strategies/hyperion_pnl.json';
import horaiPnl from '../data/strategies/horai_pnl.json';
import theiaPnl from '../data/strategies/theia_pnl.json';
import astraeusPnl from '../data/strategies/astraeus_pnl.json';
import phaethonPnl from '../data/strategies/phaethon_pnl.json';
import hemeraPnl from '../data/strategies/hemera_pnl.json';
import selenePnl from '../data/strategies/selene_pnl.json';
import asteriaPnl from '../data/strategies/asteria_pnl.json';
import aetherPnl from '../data/strategies/aether_pnl.json';
import { useAllStrategyVariants, variantCurve, STRATEGY_VARIANTS } from './StrategyVariantContext';

/**
 * Exposure of each intraday futures strategy (EOS, ARTEMIS, HELIOS), kept SEPARATELY per
 * strategy: share of the account in the Nasdaq while a position is open (50 % … 500 %). Chosen on the strategy page, or in
 * Performance / Portfolio Builder when that strategy is selected there. Remembered per browser.
 */

export const INTRADAY_PNL = { eos: eosPnl, artemis: artemisPnl, helios: heliosPnl, hyperion: hyperionPnl, horai: horaiPnl, theia: theiaPnl, astraeus: astraeusPnl, phaethon: phaethonPnl, hemera: hemeraPnl, selene: selenePnl, asteria: asteriaPnl, aether: aetherPnl };
export const INTRADAY_SLUGS = Object.keys(INTRADAY_PNL);
const STORAGE_KEY = 'hma.intradaySizing.v3';

const DEFAULTS = Object.fromEntries(INTRADAY_SLUGS.map((slug) => [slug, SIZING_DEFAULT]));

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) || {};
    const out = { ...DEFAULTS };
    for (const slug of INTRADAY_SLUGS) {
      if (parsed[slug]) out[slug] = { ...SIZING_DEFAULT, ...parsed[slug] };
    }
    return out;
  } catch {
    return DEFAULTS;
  }
}

export function isDefaultSizing(s) {
  return !s || Number(s.exposicion) === SIZING_DEFAULT.exposicion;
}

const Ctx = createContext({ all: DEFAULTS, setFor: () => {} });

export function IntradaySizingProvider({ children }) {
  const [all, setAll] = useState(load);
  const setFor = useCallback((slug, update) => {
    setAll((prev) => {
      const current = prev[slug] || SIZING_DEFAULT;
      const nextOne = typeof update === 'function' ? update(current) : { ...current, ...update };
      const next = { ...prev, [slug]: nextOne };
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ all, setFor }), [all, setFor]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Sizing of ONE intraday strategy. */
export function useIntradaySizing(slug) {
  const { all, setFor } = useContext(Ctx);
  const sizing = all[slug] || SIZING_DEFAULT;
  const setSizing = useCallback((update) => setFor(slug, update), [setFor, slug]);
  return { sizing, setSizing, isDefault: isDefaultSizing(sizing) };
}

/** Curve of one intraday strategy for a sizing (the published one when default). */
export function sizedCurve(slug, sizing, fallback) {
  const pnl = INTRADAY_PNL[slug];
  if (!pnl || isDefaultSizing(sizing)) return fallback;
  return buildIntradayCurve(pnl, sizing);
}

function applyOne(item, all, variants) {
  if (!item) return item;
  if (STRATEGY_VARIANTS[item.slug]) return { ...item, data: variantCurve(item.slug, variants[item.slug], item.data) };
  if (INTRADAY_PNL[item.slug]) return { ...item, data: sizedCurve(item.slug, all[item.slug], item.data) };
  return item;
}

function applySizing(items, all, variants) {
  return items.map((item) => applyOne(item, all, variants));
}

/**
 * The registry (strategies + benchmarks) with each intraday curve rebuilt for its own sizing, and each
 * strategy published in several versions (StrategyVariantContext) on the version chosen.
 */
export function useSizedRegistry() {
  const { all } = useContext(Ctx);
  const variants = useAllStrategyVariants();
  return useMemo(() => applySizing(registry, all, variants), [all, variants]);
}

export function useSizedStrategies() {
  const { all } = useContext(Ctx);
  const variants = useAllStrategyVariants();
  return useMemo(() => applySizing(strategies, all, variants), [all, variants]);
}

export function useSizedStrategy(strategy) {
  const { all } = useContext(Ctx);
  const variants = useAllStrategyVariants();
  return useMemo(() => applyOne(strategy, all, variants), [strategy, all, variants]);
}

/** All sizings, for pages that rebuild several curves at once (Portfolio Builder). */
export function useAllIntradaySizing() {
  return useContext(Ctx).all;
}
